import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { APPENV_URL } from 'src/util/appUrlEnv';
import TatStatus from './TatStatus';
import OverAllStatus from './OverallStatus';
import { Card, CardBody, Container, Button, Row, Col } from 'reactstrap';
import FeatherIcon from "feather-icons-react";
import jsPDF from 'jspdf';
import { toPng } from 'html-to-image';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

const colors: any = {
    MelTat: "#5156be",
    // TechnicianTat: "#FF9800",
    DealerTat: "#F44336",
    CustomerTat: "#9C27B0",
    SupplierTat: "#2196F3",
    CourierTat: "#4CAF50",
};

const colorsNew: any = {
    "Mel Tat": "#5156be",
    // "Technician Tat": "#FF9800",
    "Dealer Tat": "#F44336",
    "Customer Tat": "#9C27B0",
    "Supplier Tat": "#2196F3",
    "Courier Tat": "#4CAF50",
};

const PublicTatStatus = () => {
    const { job_id } = useParams<{ job_id: string }>();
    const [tatStatus, setTatStatus] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [jobsheetInfo, setJobsheetInfo] = useState<any>(null);

    useEffect(() => {
        if (job_id) {
            fetch(`${APPENV_URL.jobServiceUrl}/job/dealer-name-vi-number-job-number/${job_id}`, {
                method: "GET",
                headers: {
                    "Accept": "application/json",
                    "Authorization": `Bearer ${localStorage.getItem("token")}`,
                }
            })
                .then((res) => res.json())
                .then((res: any) => {
                    setJobsheetInfo(res?.data || res);
                })
                .catch((err: any) => {
                    console.error('Failed to fetch jobsheet details', err);
                });
        }
    }, [job_id]);

    useEffect(() => {
        if (job_id) {
            setLoading(true);
            fetch(`${APPENV_URL.jobServiceUrl}/job/view-job-tat/${job_id}`, {
                method: "GET",
                headers: {
                    "Accept": "application/json, text/plain, */*",
                    "Authorization": `Bearer ${localStorage.getItem("token")}`,
                }
            })
                .then((res) => {
                    if (!res.ok) {
                        throw new Error('Failed to fetch TAT status');
                    }
                    return res.json();
                })
                .then((res: any) => {
                    setTatStatus(res?.data ? res.data : (Array.isArray(res) ? res : []));
                })
                .catch((err: any) => {
                    setError(err?.message || 'Failed to fetch TAT status');
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [job_id]);

    const convertMinutes = (minutes: any) => {
        let years = Math.floor(minutes / (60 * 24 * 365));
        minutes %= (60 * 24 * 365);
        let months = Math.floor(minutes / (60 * 24 * 30));
        minutes %= (60 * 24 * 30);
        let days = Math.floor(minutes / (60 * 24));
        minutes %= (60 * 24);
        let hours = Math.floor(minutes / 60);
        let remainingMinutes = minutes % 60;
        let output = [];
        if (years > 0) output.push(`${years} year${years > 1 ? 's' : ''}`);
        if (months > 0) output.push(`${months} month${months > 1 ? 's' : ''}`);
        if (days > 0) output.push(`${days} day${days > 1 ? 's' : ''}`);
        if (hours > 0) output.push(`${hours} hour${hours > 1 ? 's' : ''}`);
        if (remainingMinutes > 0) output.push(`${remainingMinutes.toFixed(1)} minute${remainingMinutes > 1 ? 's' : ''}`);
        if (output.length === 0) return "less than a minute";
        return output.join(", ");
    };

    const totalTat = useMemo(() => {
        return tatStatus?.length > 0 ? tatStatus.reduce((acc: any, curr: any) => acc + curr.tat, 0) : 0;
    }, [tatStatus]);

    const tatStatusData = useMemo(() => {
        if (!tatStatus || tatStatus.length === 0) return [];

        const MIN_PERCENTAGE = 1;
        // const rawData = tatStatus.filter((data: any) => (data.tat >= 60) || data.to == "Technician Arrived" || data.to == "Service Request Generated to Job Sheet Created");
        const rawData = tatStatus;
        const totalTatCalculated = rawData.reduce((sum: number, data: any) => sum + data.tat, 0);

        const initialSegments = rawData.map((data: any) => ({
            ...data,
            rawPercentage: totalTatCalculated > 0 ? (data.tat / totalTatCalculated) * 100 : 0,
        }));

        const smallSegments = initialSegments.filter((seg: any) => seg.rawPercentage < MIN_PERCENTAGE);
        const largeSegments = initialSegments.filter((seg: any) => seg.rawPercentage >= MIN_PERCENTAGE);

        const totalMinAssigned = smallSegments.length * MIN_PERCENTAGE;
        const totalAvailableForLarge = 100 - totalMinAssigned;

        const totalLargeRaw = largeSegments.reduce((sum: any, seg: any) => sum + seg.rawPercentage, 0);

        const finalSegments = initialSegments.map((seg: any) => {
            let heightPercentage = 0;
            if (seg.rawPercentage < MIN_PERCENTAGE) {
                heightPercentage = MIN_PERCENTAGE;
            } else {
                heightPercentage = totalLargeRaw > 0 ? (seg.rawPercentage / totalLargeRaw) * totalAvailableForLarge : 0;
            }

            return {
                ...seg,
                heightPercentage,
                color: colors[seg.category],
            };
        });

        return finalSegments;
    }, [tatStatus]);

    const tatStatusDataNew = useMemo(() => {
        if (!tatStatus || tatStatus.length === 0) return [];

        const MIN_PERCENTAGE = 1;

        // Step 1: Filter and map to calculate initial proportional heights
        const rawData = tatStatus
            // .filter((data: any) => (data.tat >= 60) || data.to == "Technician Arrived")
            .reduce((acc: any[], curr: any) => {
                const existing = acc.find((item) => item.category === curr.category);

                if (existing) {
                    existing.tat += curr.tat;
                } else {
                    acc.push({ ...curr }); // clone to avoid mutation
                }

                return acc;
            }, []);

        const totalTatCalculated = rawData.reduce((sum: number, data: any) => sum + data.tat, 0);

        // Step 2: Calculate initial percentages
        const initialSegments = rawData.map((data: any) => ({
            ...data,
            rawPercentage: totalTatCalculated > 0 ? (data.tat / totalTatCalculated) * 100 : 0,
        }));

        // Step 3: Separate small and large segments
        const smallSegments = initialSegments.filter((seg: any) => seg.rawPercentage < MIN_PERCENTAGE);
        const largeSegments = initialSegments.filter((seg: any) => seg.rawPercentage >= MIN_PERCENTAGE);

        const totalMinAssigned = smallSegments.length * MIN_PERCENTAGE;
        const totalAvailableForLarge = 100 - totalMinAssigned;

        const totalLargeRaw = largeSegments.reduce((sum: any, seg: any) => sum + seg.rawPercentage, 0);

        // Step 4: Adjust large segments proportionally
        const finalSegments = initialSegments.map((seg: any) => {
            let heightPercentage = 0;
            if (seg.rawPercentage < MIN_PERCENTAGE) {
                heightPercentage = MIN_PERCENTAGE;
            } else {
                heightPercentage = totalLargeRaw > 0 ? (seg.rawPercentage / totalLargeRaw) * totalAvailableForLarge : 0;
            }

            return {
                ...seg,
                heightPercentage,
                color: colors[seg.category],
            };
        });

        return finalSegments;
    }, [tatStatus]);

    const handleDownloadPDF = async () => {
        const element = document.querySelector('.tat-printable-area') as HTMLElement;
        if (!element) return;

        try {
            const dataUrl = await toPng(element, { backgroundColor: '#ffffff', cacheBust: true });
            const pdf = new jsPDF('p', 'mm', 'a4');
            const imgProps = pdf.getImageProperties(dataUrl);
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

            pdf.addImage(dataUrl, 'PNG', 0, 0, pdfWidth, pdfHeight);
            pdf.save(`JobSheet_TAT_${jobsheetInfo?.jobNumber || job_id}.pdf`);
        } catch (err) {
            console.error('oops, something went wrong!', err);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
                <h4 className="text-danger">{error}</h4>
            </div>
        );
    }

    return (
        <div style={{ backgroundColor: "#f8f8fb", minHeight: "100vh", padding: "2rem" }}>
            <Container fluid className="px-5">
                <Card className="shadow-sm" style={{ maxWidth: '1600px', margin: '0 auto' }}>
                    <CardBody>
                        <div className="d-flex justify-content-end align-items-center mb-4 d-print-none">
                            {/* <h4 className="card-title mb-0">Job Sheet TAT Status</h4> */}
                            <div className="d-flex gap-2">
                                <Button color="secondary" onClick={handleDownloadPDF}>
                                    <FeatherIcon icon="download" className="icon-xs me-2" />
                                    Download PDF
                                </Button>
                                <Button color="primary" onClick={() => window.print()}>
                                    <FeatherIcon icon="printer" className="icon-xs me-2" />
                                    Print
                                </Button>
                            </div>
                        </div>

                        {tatStatusData.length > 0 ? (
                            <div className="d-flex flex-column align-items-center justify-content-center w-100">
                                <style>
                                    {`
                    @media print {
                      @page {
                        margin: 10mm;
                      }
                      body {
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                        background-color: white !important;
                      }
                      .container {
                        max-width: 100% !important;
                        padding: 0 !important;
                        margin: 0 !important;
                      }
                      .card {
                        border: none !important;
                        box-shadow: none !important;
                      }
                      .card-body {
                        padding: 0 !important;
                      }
                      .tat-printable-area {
                        zoom: 0.75;
                        margin-top: 0 !important;
                      }
                      .tat-printable-area .row {
                        display: flex !important;
                        flex-wrap: nowrap !important;
                        margin-left: 0 !important;
                        margin-right: 0 !important;
                      }
                      .tat-printable-area .col-6,
                      .tat-printable-area .col-lg-6 {
                        flex: 0 0 50% !important;
                        max-width: 50% !important;
                      }
                      .tat-printable-area .border-end {
                        border-right: 1px solid #dee2e6 !important;
                      }
                    }
                  `}
                                </style>
                                <div className="tat-printable-area w-100 mt-1 bg-white p-4">
                                    <Row>
                                        <Col lg={6} className="border-end">
                                            <TatStatus
                                                colorsNew={colorsNew}
                                                convertMinutes={convertMinutes}
                                                tatStatusData={tatStatusData}
                                                jobSheetNumber={jobsheetInfo?.jobNumber || job_id}
                                                vinNumber={jobsheetInfo?.viNumber}
                                                dealerName={jobsheetInfo?.dealerName}
                                            />
                                        </Col>
                                        <Col lg={6} className="ps-4 d-flex align-items-center">
                                            <OverAllStatus colorsNew={colorsNew} convertMinutes={convertMinutes} tatStatusData={tatStatusDataNew} totalTat={totalTat} showList={false} />
                                        </Col>
                                    </Row>
                                    <Row className="mt-2">
                                        <Col lg={12} className="text-center">
                                            <b style={{ fontSize: "12px", color: "#666" }}>
                                                * The tat will be calculated excluding national holidays and sundays except courier
                                            </b>
                                        </Col>
                                    </Row>
                                    <Row className="mt-3">
                                        <Col lg={12}>
                                            <div className="view">
                                                <div className="general-details">
                                                    <b>Detailed TAT Breakdown</b>
                                                </div>
                                                <div className="subview">
                                                    <DataTable
                                                        value={[...tatStatusDataNew.sort((a: any, b: any) => new Date(a.creationTime).getTime() - new Date(b.creationTime).getTime()), { category: 'Total TAT', tat: totalTat, isTotal: true }]}
                                                        size="small"
                                                        showGridlines
                                                        tableStyle={{ minWidth: '100%' }}
                                                    >
                                                        <Column
                                                            header="Category"
                                                            body={(rowData) => {
                                                                const categoryNameMapping: any = {
                                                                    MelTat: "Mel Tat",
                                                                    TechnicianTat: "Technician Tat",
                                                                    DealerTat: "Dealer Tat",
                                                                    CustomerTat: "Customer Tat",
                                                                    SupplierTat: "Supplier Tat",
                                                                    CourierTat: "Courier Tat",
                                                                };
                                                                return (
                                                                    <div className="d-flex align-items-center gap-2">
                                                                        {!rowData.isTotal && <div style={{ width: "12px", height: "12px", backgroundColor: rowData.color, borderRadius: "50%", border: '1px solid rgba(0,0,0,0.1)' }}></div>}
                                                                        <span style={{ fontWeight: rowData.isTotal ? "700" : "500", fontSize: '0.9rem' }}>
                                                                            {rowData.isTotal ? "Total TAT" : (categoryNameMapping[rowData.category] || rowData.category)}
                                                                        </span>
                                                                    </div>
                                                                )
                                                            }}
                                                        />
                                                        <Column
                                                            header="Duration"
                                                            body={(rowData) => (
                                                                <span style={{ fontWeight: rowData.isTotal ? "700" : "400", fontSize: '0.9rem' }}>
                                                                    {convertMinutes(((rowData.tat ? rowData.tat : 0) + 1) / 60)}
                                                                </span>
                                                            )}
                                                        />
                                                    </DataTable>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-5">
                                <h5>No TAT Data Available for this Job Sheet</h5>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default PublicTatStatus;
